import React, { useContext, useEffect } from 'react'
import { ModalActions } from '../../common/modal/Modal'
import { t } from '../../../i18n/i18n'
import { Button } from '@fluentui/react-components'
import { useForm } from 'react-hook-form'
import { AddSwotDTO, RiskAnalysis, RiskAnalysisStatus, SWOT, SWOTType } from '../../../api/schemas/schema'
import { enumValuesToOptions } from '../../../helpers/enumHelper'
import { ControlledDropDown } from '../../common/inputs/ControlledDropdown'
import { ControlledTextInput } from '../../common/inputs/TextInput'
import { GridItem } from '../../common/grid/GridItem'
import { Grid } from '../../common/grid/Grid'
import { api } from '../../../helpers/apiHelper'
import { DataContext } from '../../../context/DataContext'
import { handleMessage } from '../../../helpers/stateHelper'
import { useSearchParams } from 'react-router-dom'
import { deleteSwot, isValidSwotType } from '../../../helpers/swotHelper'
import { IItemState } from '../../../interfaces/IItemState'
import { DraggableModal } from '../../common/modal/DraggableModal'
import { isSystemAdmin } from '../../../helpers/permissionHelper'
import { MoreOptionsMenuButton } from '../../common/other/MoreOptionsMenuButton'
import { Form } from '../../common/form/Form'

type ModalSWOTProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysisState: IItemState<RiskAnalysis>
   open: boolean
   close: () => void
   selectedItem: SWOT
}
export const ModalSWOT = ({ setRiskAnalysisState, analysisState, open, close, selectedItem }: ModalSWOTProps) => {
   const analysis = analysisState.item
   const {
      control,
      reset,
      handleSubmit,
      setValue,
      formState: { disabled, isDirty, isSubmitting },
   } = useForm<SWOT>({
      disabled:
         analysisState.disabled || analysis.status === RiskAnalysisStatus.Completed || (selectedItem?.determined && selectedItem?.proposedAction !== null),
   })
   const dataContext = useContext(DataContext)
   const {
      userAccount,
   } = dataContext.state
   const [searchParams] = useSearchParams()
   const selectedType = searchParams.get('type')

   useEffect(() => {
      if (selectedItem) reset(selectedItem)
      else reset({})
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedItem])
   useEffect(() => {
      if (!open) {
         reset({})
      } else if (selectedType && !selectedItem && isValidSwotType(Number(selectedType))) {
         setValue('type', Number(selectedType))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open, selectedItem, selectedType])

   const onSubmit = async (data: SWOT) => {
      try {
         let riskAnalysis: RiskAnalysis
         data.modifiedBy = userAccount
         data.modifiedById = userAccount.id
         if (data.id) {
            riskAnalysis = (await api().updateSwotForRiskAnalysis(analysis.id, data)).data
         } else {
            data.createdBy = userAccount
            data.createdById = userAccount.id

            const addSwotDto: AddSwotDTO = {
               swot: data,
            }

            riskAnalysis = (await api().addSwotToRiskAnalysis(analysis.id, addSwotDto)).data
         }
         close()
         setRiskAnalysisState((prev) => ({ ...prev, item: riskAnalysis }))
         reset()
         handleMessage(dataContext.setRootState, 'success', t(data.id ? 'ItemSuccessfullyUpdated' : 'ItemSuccessfullySaved', { Item: t('SWOT') }))
      } catch (error) {
         handleMessage(dataContext.setRootState, 'error', t(data.id ? 'CouldNotUpdateItem' : 'CouldNotCreateItem', { Item: t('SWOT') }))
      }
   }

   const closeAndReset = () => {
      close()
      reset()
   }

   const deleteOptions = () => {
      const swotInUse = analysis.actionPlans?.some((ap) => ap.swotId === selectedItem.id)
      const userHasPermission = isSystemAdmin(userAccount) || analysis.analysisOwner?.id === userAccount?.id
      const deleteDisabled = !userHasPermission || swotInUse
      const deleteTooltipMessage = deleteDisabled
         ? !userHasPermission
            ? t('OnlySystemAdministratorOrRiskOwnerCanDeleteItem', { Item: t('SWOT') })
            : t('ItemIsConnectedToItemAndCantBeDeleted', { ItemA: t('SWOT'), Article: t('A').toLowerCase(), ItemB: t('ActionPlan').toLowerCase() })
         : ''

      return (
         <MoreOptionsMenuButton
            item={selectedItem}
            deleteFunction={deleteSwot}
            setState={setRiskAnalysisState}
            closeOnDelete={close}
            disabled={deleteDisabled}
            disabledToolTipMessage={deleteTooltipMessage}
            deleteMessage={t('DeleteItem', { Item: t('SWOT') })}
            deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('SWOT') })}
         />
      )
   }

   return (
      <DraggableModal title={t('Identify')} open={open} onClose={closeAndReset} secondaryButton={selectedItem ? deleteOptions : null}>
         <Form onSubmit={handleSubmit(onSubmit)}>
            <Grid>
               <GridItem size="12/12">
                  <ControlledDropDown control={control} label={t('SWOT')} name={'type'} options={enumValuesToOptions(SWOTType)} required />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledTextInput control={control} label={t('Title')} name={'title'} required />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledTextInput control={control} label={t('Description')} name={'description'} multiline required />
               </GridItem>
               <GridItem size="12/12">
                  <ModalActions>
                     <Button onClick={closeAndReset} disabled={isSubmitting}>
                        {disabled ? t('Close') : t('Cancel')}
                     </Button>
                     {!disabled && (
                        <Button type="submit" appearance="primary" disabled={!isDirty || isSubmitting}>
                           {selectedItem ? t('Update') : t('Save')}
                        </Button>
                     )}
                  </ModalActions>
               </GridItem>
            </Grid>
         </Form>
      </DraggableModal>
   )
}
